import { apiSlice } from './apiSlice';
import { 
  ApplicationCargo, 
  CargoResponse, 
  CargoFilters, 
  CargoHistoryEntry, 
  UpdateCargoStatusRequest, 
  CargoDetails,
  ApiResponse 
} from '../../types';
import type { CargoLifecycleProjection } from '../../services/cargoLifecycle/cargoLifecycleProjectionTypes';

export interface CargoImportRow {
  name: string;
  description?: string;
  quantity: number;
  weightPerItem: number;
  lengthM?: number;
  widthM?: number;
  heightM?: number;
  packagingType?: string;
  cargoCharacter?: string;
}

export interface CargoImportResult {
  success: boolean;
  data?: CargoImportRow[];
  errors?: string[];
  message?: string;
}

export const cargoApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    
    // Получить все грузы с фильтрами и пагинацией
    getAllCargo: builder.query<ApiResponse<CargoResponse>, CargoFilters>({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        
        // Добавляем фильтры в URL параметры
        if (filters.status) {
          if (Array.isArray(filters.status)) {
            filters.status.forEach(status => params.append('status', status));
          } else {
            params.append('status', filters.status);
          }
        }
        
        if (filters.applicationId) params.append('applicationId', filters.applicationId.toString());
        if (filters.routeId) params.append('routeId', filters.routeId.toString());
        if (filters.warehouseId) params.append('warehouseId', filters.warehouseId.toString());
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
        if (filters.search) params.append('search', filters.search);
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.limit) params.append('limit', filters.limit.toString());
        
        return {
          url: `/cargo?${params.toString()}`,
          method: 'GET'
        };
      },
      providesTags: ['Cargo']
    }),

    // Массово переместить грузы на склад (admin)
    moveCargoToWarehouseBulk: builder.mutation<{ movedCount: number; warehouseId: number }, { warehouseId: number; cargoIds: number[] }>({
      query: (data) => ({
        url: '/cargo/move-to-warehouse',
        method: 'POST',
        body: data
      }),
      transformResponse: (response: ApiResponse<{ movedCount: number; warehouseId: number }>) => response.data!,
      invalidatesTags: ['Cargo', 'Application']
    }),

    /** CARGO-L2/L3: derived lifecycle counters (SSOT for UI when projection flag on). */
    getCargoLifecycleProjection: builder.query<CargoLifecycleProjection, number>({
      query: (id) => ({
        url: `/cargo/${id}/lifecycle-projection`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<CargoLifecycleProjection>) => response.data!,
      providesTags: (result, error, id) => [{ type: 'Cargo', id: `projection-${id}` }],
    }),

    // Получить груз по ID
    getCargoById: builder.query<CargoDetails, number>({
      query: (id) => ({
        url: `/cargo/${id}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<CargoDetails>) => response.data!,
      providesTags: (result, error, id: number) => [{ type: 'Cargo', id }]
    }),

    // Получить историю перемещений груза
    getCargoHistory: builder.query<ApiResponse<CargoHistoryEntry[]>, { cargoId: number }>({
      query: ({ cargoId }) => ({
        url: `/cargo/${cargoId}/history`,
        method: 'GET'
      }),
      providesTags: (result, error, { cargoId }) => [
        { type: 'Cargo', id: `history-${cargoId}` },
        'CargoHistory'
      ]
    }),

    // Обновить статус груза
    updateCargoStatus: builder.mutation<ApplicationCargo, { id: number } & UpdateCargoStatusRequest>({
      query: ({ id, ...data }) => ({
        url: `/cargo/${id}/status`,
        method: 'PATCH',
        body: data
      }),
      transformResponse: (response: ApiResponse<ApplicationCargo>) => response.data!,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Cargo', id },
        'Cargo',
        { type: 'Cargo', id: `history-${id}` }
      ]
    }),

    // Получить грузы по заявке
    getCargoByApplication: builder.query<ApplicationCargo[], number>({
      query: (applicationId) => ({
        url: `/cargo/application/${applicationId}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<ApplicationCargo[]>) => response.data || [],
      providesTags: (result, error, applicationId: number) => [
        { type: 'Cargo', id: `application-${applicationId}` }
      ]
    }),

    // Получить грузы по статусу
    getCargoByStatus: builder.query<ApplicationCargo[], string>({
      query: (status) => ({
        url: `/cargo/status/${status}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<ApplicationCargo[]>) => response.data || [],
      providesTags: (result, error, status: string) => [
        { type: 'Cargo', id: `status-${status}` }
      ]
    }),

    // Получить грузы на складе
    getCargoOnWarehouse: builder.query<ApplicationCargo[], number>({
      query: (warehouseId) => ({
        url: `/cargo/warehouse/${warehouseId}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<ApplicationCargo[]>) => response.data || [],
      providesTags: (result, error, warehouseId: number) => [
        { type: 'Cargo', id: `warehouse-${warehouseId}` }
      ]
    }),

    // Получить грузы в маршруте
    getCargoInRoute: builder.query<ApplicationCargo[], number>({
      query: (routeId) => ({
        url: `/cargo/route/${routeId}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<ApplicationCargo[]>) => response.data || [],
      providesTags: (result, error, routeId: number) => [
        { type: 'Cargo', id: `route-${routeId}` }
      ]
    }),

    // Обновить груз
    updateCargo: builder.mutation<ApplicationCargo, { id: number; data: Partial<ApplicationCargo> }>({
      query: ({ id, data }) => ({
        url: `/cargo/${id}`,
        method: 'PUT',
        body: data
      }),
      transformResponse: (response: ApiResponse<ApplicationCargo>) => response.data!,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Cargo', id },
        'Cargo',
        { type: 'Application', id: result?.applicationId }
      ]
    }),

    // Импорт грузов из Excel файла
    importCargoFromExcel: builder.mutation<CargoImportResult, FormData>({
      query: (formData) => ({
        url: '/cargo/import-excel',
        method: 'POST',
        body: formData
      }),
      // Сервер иногда возвращает структуру { success, data, errors, message }
      // а иногда оборачивает в общий ApiResponse { data: { ... } }.
      // Чтобы быть устойчивыми к обоим вариантам, возвращаем либо response.data, либо сам response.
      transformResponse: (response: any) => {
        if (response && typeof response === 'object') {
          if (response.data && typeof response.data.success !== 'undefined') {
            return response.data as CargoImportResult;
          }
          if (typeof response.success !== 'undefined') {
            return response as CargoImportResult;
          }
        }
        return response as CargoImportResult;
      }
    }),

    // Скачать шаблон для импорта грузов
    downloadCargoImportTemplate: builder.query<Blob, void>({
      query: () => ({
        url: '/cargo/import-template',
        method: 'GET',
        responseType: 'blob'
      })
    })

  })
});

export const {
  useGetAllCargoQuery,
  useGetCargoLifecycleProjectionQuery,
  useGetCargoByIdQuery,
  useGetCargoHistoryQuery,
  useUpdateCargoStatusMutation,
  useMoveCargoToWarehouseBulkMutation,
  useUpdateCargoMutation,
  useGetCargoByApplicationQuery,
  useGetCargoByStatusQuery,
  useGetCargoOnWarehouseQuery,
  useGetCargoInRouteQuery,
  useImportCargoFromExcelMutation,
  useDownloadCargoImportTemplateQuery
} = cargoApi;